import { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Leaf, Mail, Lock, User, Phone, ArrowLeft, Eye, EyeOff, Gift, Building, FileText, Receipt, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export const AuthPage = () => {
  const { t } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, register, resendVerification } = useAuth();
  
  // Get referral code from URL if present
  const referralFromUrl = searchParams.get('ref') || '';
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    phone: '',
    referral_code: referralFromUrl,
    // Business fields
    is_business: false,
    company_name: '',
    vat_number: '',
    billing_address: '',
    billing_type: 'per_trip'
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error(t('auth_forgot_desc').split('.')[0]);
      return;
    }
    
    setResetLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      
      if (response.ok) {
        toast.success('Un email de réinitialisation vous a été envoyé !');
        setShowForgotPassword(false);
        setResetEmail('');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur de connexion au serveur');
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Connexion réussie !');
        // Redirect to booking or previous page
        const redirectParam = searchParams.get('redirect');
        const from = redirectParam || location.state?.from || '/booking';
        navigate(from);
      } else {
        // Check password confirmation
        if (formData.password !== formData.confirm_password) {
          toast.error('Les mots de passe ne correspondent pas');
          setLoading(false);
          return;
        }
        
        // Check password strength
        if (formData.password.length < 8) {
          toast.error('Le mot de passe doit contenir au moins 8 caractères');
          setLoading(false);
          return;
        }
        
        // Check phone number
        if (!formData.phone) {
          toast.error('Le numéro de téléphone est requis pour la vérification');
          setLoading(false);
          return;
        }
        
        // Validate business fields
        if (formData.is_business && (!formData.company_name || !formData.vat_number)) {
          toast.error('Veuillez remplir les informations entreprise');
          setLoading(false);
          return;
        }
        
        const result = await register(formData);
        
        // Show email verification message
        if (result.requires_verification) {
          setRegisteredEmail(formData.email);
          toast.success('Inscription réussie ! Vérifiez votre email pour activer votre compte.');
          setShowVerificationMessage(true);
        }
      }
    } catch (error) {
      // Handle verification required
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail;
        if (detail?.requires_verification) {
          toast.error('Veuillez confirmer votre email avant de vous connecter');
          // Show verification message
          setRegisteredEmail(detail.email || formData.email);
          setShowVerificationMessage(true);
          setLoading(false);
          return;
        }
      }
      const message = error.response?.data?.detail?.message || error.response?.data?.detail || 'Une erreur est survenue';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Show verification message after registration
  if (showVerificationMessage) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="glass-card rounded-2xl p-8">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-12 h-12 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">{t('auth_verify_email')}</h1>
            <p className="text-zinc-400 mb-6">
              {t('auth_verify_email_sent')} <strong className="text-emerald-400">{registeredEmail}</strong>
            </p>
            <p className="text-zinc-500 text-sm mb-6">
              {t('auth_verify_email_click')}
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => {
                  setShowVerificationMessage(false);
                  setIsLogin(true);
                }}
                className="btn-primary w-full"
              >
                {t('auth_verified_login')}
              </Button>
              <button
                onClick={async () => {
                  try {
                    await resendVerification(registeredEmail);
                    toast.success('Email renvoyé !');
                  } catch (e) {
                    toast.error('Erreur');
                  }
                }}
                className="text-emerald-500 hover:text-emerald-400 text-sm"
              >
                {t('auth_resend')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col p-6 lg:p-12 overflow-y-auto">
        {/* Back button */}
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8 w-fit"
          data-testid="back-home-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('back_home')}
        </button>

        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <img src="/logo-youmi.png" alt="YOUMI GREEN VTC" className="w-14 h-14" />
            <span className="font-bold text-xl font-['Outfit'] text-white">YOUMI GREEN</span>
            <span className="text-emerald-500 font-bold text-xl font-['Outfit']">VTC</span>
          </div>

          <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-2">
            {isLogin ? t('auth_welcome') : t('auth_create')}
          </h1>
          <p className="text-zinc-400 mb-8">
            {isLogin 
              ? t('auth_login_subtitle') 
              : t('auth_register_subtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-zinc-300">{t('auth_firstname')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="Jean"
                      required={!isLogin}
                      className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                      data-testid="firstname-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-zinc-300">{t('auth_lastname')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder="Dupont"
                      required={!isLogin}
                      className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                      data-testid="lastname-input"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">{t('auth_email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="vous@exemple.com"
                  required
                  className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                  data-testid="email-input"
                />
              </div>
            </div>

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-zinc-300">{t('auth_phone')}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="06 12 34 56 78"
                    required={!isLogin}
                    className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                    data-testid="phone-input"
                  />
                </div>
              </div>
            )}

            {/* Business Account Toggle */}
            {!isLogin && (
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <div className="flex items-center space-x-3">
                  <Checkbox
                    id="is_business"
                    name="is_business"
                    checked={formData.is_business}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_business: checked })}
                    className="border-blue-500"
                  />
                  <label
                    htmlFor="is_business"
                    className="text-sm font-medium text-blue-400 cursor-pointer flex items-center gap-2"
                  >
                    <Building className="w-4 h-4" />
                    {t('auth_business_account')}
                  </label>
                </div>
                
                {formData.is_business && (
                  <div className="mt-4 space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_name" className="text-zinc-300">{t('auth_company_name')}</Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input
                          id="company_name"
                          name="company_name"
                          value={formData.company_name}
                          onChange={handleChange}
                          placeholder="Ma Société SARL"
                          required={formData.is_business}
                          className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                          data-testid="company-name-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="vat_number" className="text-zinc-300">{t('auth_vat_number')}</Label>
                      <div className="relative">
                        <Receipt className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input
                          id="vat_number"
                          name="vat_number"
                          value={formData.vat_number}
                          onChange={handleChange}
                          placeholder="FR12345678901"
                          required={formData.is_business}
                          className="pl-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                          data-testid="vat-input"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="billing_address" className="text-zinc-300">{t('auth_billing_address')}</Label>
                      <Input
                        id="billing_address"
                        name="billing_address"
                        value={formData.billing_address}
                        onChange={handleChange}
                        placeholder="123 Rue Example, 06000 Nice"
                        className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                        data-testid="billing-address-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-zinc-300">{t('auth_billing_type')}</Label>
                      <RadioGroup 
                        value={formData.billing_type} 
                        onValueChange={(value) => setFormData({ ...formData, billing_type: value })}
                        className="flex gap-4"
                      >
                        <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${
                          formData.billing_type === 'per_trip' 
                            ? 'border-emerald-500 bg-emerald-500/10' 
                            : 'border-zinc-700'
                        }`}>
                          <RadioGroupItem value="per_trip" id="per_trip" />
                          <span className="text-sm text-zinc-300">{t('auth_per_trip')}</span>
                        </label>
                        <label className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${
                          formData.billing_type === 'monthly' 
                            ? 'border-emerald-500 bg-emerald-500/10' 
                            : 'border-zinc-700'
                        }`}>
                          <RadioGroupItem value="monthly" id="monthly" />
                          <span className="text-sm text-zinc-300">{t('auth_monthly')}</span>
                        </label>
                      </RadioGroup>
                    </div>

                    <div className="p-3 bg-zinc-800 rounded-lg">
                      <p className="text-xs text-zinc-400">
                        <FileText className="w-3 h-3 inline mr-1" />
                        Les factures seront émises en <strong className="text-emerald-400">HT (Hors Taxes)</strong> avec TVA 10% applicable au transport.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="referral_code" className="text-zinc-300 flex items-center gap-2">
                  <Gift className="w-4 h-4 text-emerald-500" />
                  {t('auth_referral_code')}
                </Label>
                <Input
                  id="referral_code"
                  name="referral_code"
                  value={formData.referral_code}
                  onChange={handleChange}
                  placeholder="Ex: YOUMIAB1234"
                  className="bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                  data-testid="referral-input"
                />
                <p className="text-xs text-emerald-400">
                  {t('auth_referral_hint')}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">{t('auth_password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  className="pl-10 pr-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {!isLogin && (
                <p className="text-xs text-zinc-500">{t('auth_min_chars')}</p>
              )}
            </div>

            {/* Confirm Password - Registration only */}
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="confirm_password" className="text-zinc-300">{t('auth_confirm_password')}</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="confirm_password"
                    name="confirm_password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirm_password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    className="pl-10 pr-10 bg-zinc-900 border-zinc-700 text-white placeholder:text-zinc-500"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                    data-testid="toggle-confirm-password-btn"
                  >
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formData.password && formData.confirm_password && formData.password !== formData.confirm_password && (
                  <p className="text-xs text-red-400">{t('auth_passwords_mismatch')}</p>
                )}
              </div>
            )}

            {/* Forgot Password - Login only */}
            {isLogin && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-emerald-500 hover:text-emerald-400"
                  data-testid="forgot-password-btn"
                >
                  {t('auth_forgot_password')}
                </button>
              </div>
            )}

            <Button 
              type="submit" 
              className="btn-primary w-full py-3"
              disabled={loading || (!isLogin && formData.password !== formData.confirm_password)}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  {t('auth_loading')}
                </span>
              ) : (
                isLogin ? t('auth_login_btn') : t('auth_register_btn')
              )}
            </Button>

            {/* Separator */}
            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-zinc-700" />
              <span className="text-zinc-500 text-xs uppercase">ou</span>
              <div className="flex-1 h-px bg-zinc-700" />
            </div>

            {/* Google Sign-In Button */}
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + '/client';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 transition-colors"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span className="text-white font-medium text-sm">
                {isLogin ? 'Se connecter avec Google' : "S'inscrire avec Google"}
              </span>
            </button>
          </form>

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <div className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full border border-zinc-700">
                <h2 className="text-xl font-bold text-white mb-2">{t('auth_forgot_title')}</h2>
                <p className="text-zinc-400 text-sm mb-4">
                  {t('auth_forgot_desc')}
                </p>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="vous@exemple.com"
                      required
                      className="pl-10 bg-zinc-800 border-zinc-600 text-white"
                      data-testid="reset-email-input"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowForgotPassword(false)}
                      className="flex-1 border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                    >
                      {t('auth_cancel')}
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 btn-primary"
                      disabled={resetLoading}
                    >
                      {resetLoading ? t('auth_sending') : t('auth_send_link')}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <p className="text-center text-zinc-400 mt-6">
            {isLogin ? t('auth_no_account') : t('auth_has_account')}{' '}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-emerald-500 hover:text-emerald-400 font-medium"
              data-testid="toggle-auth-mode-btn"
            >
              {isLogin ? t('auth_register_btn') : t('auth_login_btn')}
            </button>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-transparent" />
        <img
          src="https://images.unsplash.com/photo-1630176028338-bd36cf3ed160?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxjaGF1ZmZldXIlMjBvcGVuaW5nJTIwY2FyJTIwZG9vciUyMGx1eHVyeSUyMHNlcnZpY2V8ZW58MHx8fHwxNzcxNTkwMjY4fDA&ixlib=rb-4.1.0&q=85"
          alt="Service VTC Nice - Chauffeur privé"
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-0 left-0 right-0 p-12 bg-gradient-to-t from-black/80 to-transparent">
          <blockquote className="text-xl text-white font-['Outfit'] italic">
            "Un service impeccable, ponctuel et écologique. Facturation mensuelle parfaite pour notre entreprise."
          </blockquote>
          <p className="text-zinc-400 mt-4">— Direction, Hôtel Le Negresco, Nice</p>
        </div>
      </div>
    </div>
  );
};
